import { useEffect, useState } from "react";
import {
  Link,
  Outlet,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import {
  BarChart3,
  Briefcase,
  Database,
  LogOut,
  Menu,
  PhoneCall,
  Users,
  X,
} from "lucide-react";

import { Button } from "@/shared/components/ui/button";
import { logoutThunk } from "@/modules/auth/store/authThunk";
import { getMyFeaturePermissions } from "@/modules/team/api/teamMemberFeaturePermissionApi";
import type { RootState } from "@/store/store";
import DashboardHeader from "./DashboardHeader";

const PERMISSION_MANAGED_ROLES = [
  "recruiter",
  "operations",
  "manager",
] as const;

type ModuleFeatureKey =
  | "DASHBOARD"
  | "CANDIDATES"
  | "VOICE_CALLS"
  | "JOBS"
  | "RESUME_DATABASE";

export default function HRLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch<any>();

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [allowedFeatures, setAllowedFeatures] = useState<Set<string>>(
    new Set()
  );
  const [permissionsLoading, setPermissionsLoading] = useState(false);
  const [permissionsLoaded, setPermissionsLoaded] = useState(false);

  const user = useSelector((state: RootState) => state.auth.user);
  const normalizedRole = user?.role?.trim().toLowerCase() || "";
  const isHRAdmin = normalizedRole === "hr";

  const isPermissionManagedUser = PERMISSION_MANAGED_ROLES.includes(
    normalizedRole as (typeof PERMISSION_MANAGED_ROLES)[number]
  );

  const pathname = location.pathname;

  const isCandidateDetailsPage =
    /^\/hr\/candidates\/[^/]+$/.test(pathname);

  /**
   * Fetch module permissions for Recruiter, Operations, and Manager.
   * HR Admin and Admin retain their existing role-based access behavior.
   * A failed permission request is treated as no module access.
   */
  useEffect(() => {
    if (!isPermissionManagedUser) {
      setAllowedFeatures(new Set());
      setPermissionsLoading(false);
      setPermissionsLoaded(true);
      return;
    }

    let cancelled = false;

    setPermissionsLoading(true);
    setPermissionsLoaded(false);
    setAllowedFeatures(new Set());

    const loadPermissions = async () => {
      try {
        const permissions = await getMyFeaturePermissions();

        if (cancelled) return;

        const allowed = new Set(
          permissions
            .filter(
              (permission) => permission.permissionLevel === "ALLOWED"
            )
            .map((permission) => permission.featureKey)
        );

        setAllowedFeatures(allowed);
      } catch (error) {
        console.error(
          "Failed to load team member feature permissions:",
          error
        );

        if (!cancelled) {
          setAllowedFeatures(new Set());
        }
      } finally {
        if (!cancelled) {
          setPermissionsLoading(false);
          setPermissionsLoaded(true);
        }
      }
    };

    void loadPermissions();

    return () => {
      cancelled = true;
    };
  }, [isPermissionManagedUser, normalizedRole, user?.id]);

  /**
   * Each sidebar item is linked to its module permission.
   * Register new modules here when they are connected to a route
   * and frontend entry point.
   */
  const moduleNavItems: {
    href: string;
    icon: typeof BarChart3;
    label: string;
    featureKey: ModuleFeatureKey;
  }[] = [
      {
        href: "/hr",
        icon: BarChart3,
        label: "Dashboard",
        featureKey: "DASHBOARD",
      },
      {
        href: "/hr/candidates",
        icon: Users,
        label: "Candidate Pool",
        featureKey: "CANDIDATES",
      },
      // {
      //   href: "/hr/voice-calls",
      //   icon: PhoneCall,
      //   label: "Voice Calls",
      //   featureKey: "VOICE_CALLS",
      // },
      {
        href: "/hr/jobs",
        icon: Briefcase,
        label: "Jobs",
        featureKey: "JOBS",
      },
     {
      href: "/hr/resume-database",
      icon: Database,
      label: "Master Database",
      featureKey: "RESUME_DATABASE",
    },
    ];

  const navItems = moduleNavItems
    .filter(
      (item) =>
        !isPermissionManagedUser ||
        allowedFeatures.has(item.featureKey)
    )
    .map((item) => ({
      href: item.href,
      icon: item.icon,
      label: item.label,
    }));

  // My Team is restricted to HR Admin.
  if (isHRAdmin) {
    navItems.push({
      href: "/hr/my-team",
      icon: Users,
      label: "My Team",
    });
  }

  const sidebarHomeHref = navItems[0]?.href || "/hr";

  /**
   * Resolve the feature required by the current page.
   * Candidate and job subpages inherit their parent module permission.
   */
  const getRequiredFeatureKey = (
    path: string
  ): ModuleFeatureKey | null => {
    if (path === "/hr") return "DASHBOARD";
    if (path.startsWith("/hr/candidates")) return "CANDIDATES";
    if (path.startsWith("/hr/voice-calls")) return "VOICE_CALLS";
    if (path.startsWith("/hr/jobs")) return "JOBS";
    if (path.startsWith("/hr/resume-database")) {
      return "RESUME_DATABASE";
    }

    return null;
  };

  const requiredFeatureKey = getRequiredFeatureKey(pathname);

  const isCurrentModuleDenied =
    isPermissionManagedUser &&
    permissionsLoaded &&
    requiredFeatureKey !== null &&
    !allowedFeatures.has(requiredFeatureKey);

  const firstAvailableHref = navItems[0]?.href;

  // Redirect away from a restricted URL after permissions load.
  useEffect(() => {
    if (!isCurrentModuleDenied || !permissionsLoaded) return;

    if (firstAvailableHref && pathname !== firstAvailableHref) {
      navigate(firstAvailableHref, { replace: true });
    }
  }, [
    firstAvailableHref,
    isCurrentModuleDenied,
    navigate,
    pathname,
    permissionsLoaded,
  ]);

  const getCurrentPage = () => {
    if (pathname === "/hr") {
      return { title: "Dashboard", icon: BarChart3 };
    }

    if (pathname.startsWith("/hr/candidates")) {
      return { title: "Candidate Pool", icon: Users };
    }

    // if (pathname.startsWith("/hr/voice-calls")) {
    //   return { title: "Voice Calls", icon: PhoneCall };
    // }

    if (pathname.startsWith("/hr/jobs")) {
      return { title: "Job Management", icon: Briefcase };
    }

    if (pathname.startsWith("/hr/resume-database")) {
       return { title: "Master Database", icon: Database };
    }

    if (pathname.startsWith("/hr/my-team")) {
      return { title: "My Team", icon: Users };
    }

    return { title: "Dashboard", icon: BarChart3 };
  };

  const currentPage = getCurrentPage();

  const handleLogout = async () => {
    try {
      await dispatch(logoutThunk()).unwrap();
    } catch (error) {
      console.error("Logout failed:", error);
    } finally {
      setSidebarOpen(false);
      navigate("/", { replace: true });
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Sidebar overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 md:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`
          fixed left-0 top-0 z-50
          flex h-screen w-64 flex-col
          border-r border-slate-200
          bg-white shadow-lg
          transition-transform duration-300
          ${sidebarOpen ? "translate-x-0" : "-translate-x-full"}
          md:translate-x-0
        `}
      >
        {/* Sidebar header */}
        <div className="flex h-16 shrink-0 items-center justify-between border-b border-slate-200 px-4">
          <Link
            to={sidebarHomeHref}
            onClick={() => setSidebarOpen(false)}
            className="flex items-center gap-2.5"
          >
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-gradient-to-br from-blue-600 to-indigo-600">
              <BarChart3 className="h-5 w-5 text-white" />
            </div>

            <div>
              <h2 className="font-bold text-slate-900">
                HR Dashboard
              </h2>
              <p className="text-xs text-slate-500">
                Dharohar Land Corp
              </p>
            </div>
          </Link>

          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="md:hidden"
            onClick={() => setSidebarOpen(false)}
          >
            <X className="h-5 w-5" />
          </Button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 space-y-1 overflow-y-auto p-3">
          {navItems.map((item) => {
            const Icon = item.icon;

            const isActive =
              pathname === item.href ||
              (item.href !== "/hr" &&
                pathname.startsWith(`${item.href}/`));

            return (
              <Link
                key={item.href}
                to={item.href}
                onClick={() => setSidebarOpen(false)}
                className={`
                  flex items-center gap-3 rounded-lg
                  px-3 py-2.5
                  text-sm font-medium
                  transition-colors
                  ${isActive
                    ? "bg-blue-50 text-blue-700"
                    : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
                  }
                `}
              >
                <Icon className="h-5 w-5 shrink-0" />
                <span>{item.label}</span>
              </Link>
            );
          })}
        </nav>

        {/* Logout */}
        <div className="shrink-0 border-t border-slate-200 p-3">
          <Button
            type="button"
            variant="outline"
            className="w-full text-red-600 hover:bg-red-50 hover:text-red-700"
            onClick={handleLogout}
          >
            <LogOut className="mr-2 h-4 w-4" />
            Logout
          </Button>
        </div>
      </aside>

      {/* Mobile menu */}
      <div className="fixed left-3 top-3 z-[60] md:hidden">
        <Button
          type="button"
          variant="outline"
          size="icon"
          className="h-10 w-10 rounded-lg bg-white shadow-sm"
          onClick={() => setSidebarOpen(true)}
        >
          <Menu className="h-5 w-5" />
        </Button>
      </div>

      {/* Main content */}
      <div className="md:ml-64">
        {!isCandidateDetailsPage && (
          <DashboardHeader
            user={user}
            pageTitle={currentPage.title}
            pageIcon={currentPage.icon}
          />
        )}

        <main
          className={
            isCandidateDetailsPage
              ? "min-h-screen"
              : "min-h-[calc(100vh-64px)]"
          }
        >
          {isPermissionManagedUser &&
            (!permissionsLoaded || permissionsLoading) ? (
            <div className="flex min-h-64 items-center justify-center px-6">
              <p className="text-sm text-slate-500">
                Loading your module permissions...
              </p>
            </div>
          ) : isCurrentModuleDenied ? (
            navItems.length > 0 ? (
              <div className="p-6">
                <p className="text-sm text-slate-600">
                  This module is not available for your account.
                  Redirecting to an available module...
                </p>
              </div>
            ) : (
              <div className="flex min-h-64 items-center justify-center px-6">
                <div className="max-w-md text-center">
                  <h2 className="text-lg font-semibold text-slate-900">
                    No modules assigned
                  </h2>

                  <p className="mt-2 text-sm text-slate-500">
                    Your HR Admin has not granted access to any
                    modules yet. Please contact your HR Admin.
                  </p>
                </div>
              </div>
            )
          ) : (
            <Outlet />
          )}
        </main>
      </div>
    </div>
  );
}